import { FC, useState, useMemo } from 'react';
import SectionWrapper from '../components/SectionWrapper';
import {
  Stack,
  Title,
  Text,
  Grid,
  Textarea,
  Paper,
  Loader,
  Tooltip,
  useMantineTheme,
  Button,
  Group,
  ScrollArea,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import ClassificationAPI, { TopicPosition } from '@apis/ClassificationAPI';
import { useQuery } from '@tanstack/react-query';

interface HighlightedTextProps {
  text: string;
  topicPositions: TopicPosition[];
  selectedTopic?: string | null;
  sentences: Array<{ text: string; startChar: number; endChar: number }>;
}

const HighlightedText: FC<HighlightedTextProps> = ({ text, topicPositions, selectedTopic, sentences }) => {
  const theme = useMantineTheme();
  const isDark = theme.colorScheme === 'dark';

  // Convert word indices (relative to sentence) to character positions (relative to full text)
  // The API creates sentences by joining words with spaces: " ".join(textbox.words)
  // So word positions are relative to the sentence after this joining
  const charPositions = useMemo(() => {
    return topicPositions.map((pos) => {
      // Get the sentence for this position
      const sentence = sentences[pos.sentenceIndex];
      if (!sentence) {
        return { start: 0, end: 0, topic: pos.topic };
      }

      // Get the actual sentence text from the original text
      const sentenceText = text.substring(sentence.startChar, sentence.endChar);

      // Split sentence into words (matching how the API processes it)
      // The API splits on spaces after autocorrect processing
      // We'll split on whitespace to get words
      const sentenceWords: Array<{ word: string; startChar: number; endChar: number }> = [];
      const wordRegex = /\S+/g;
      let match;
      
      while ((match = wordRegex.exec(sentenceText)) !== null) {
        sentenceWords.push({
          word: match[0],
          startChar: match.index, // relative to sentence start in original text
          endChar: match.index + match[0].length, // relative to sentence start in original text
        });
      }

      // Check if word indices are valid for this sentence
      if (pos.start >= sentenceWords.length || pos.start < 0) {
        return { start: 0, end: 0, topic: pos.topic };
      }

      // Get the start word (relative to sentence)
      const startWord = sentenceWords[pos.start];
      if (!startWord) {
        return { start: 0, end: 0, topic: pos.topic };
      }

      // Get the end word (inclusive, relative to sentence)
      const endWordIndex = Math.min(pos.end, sentenceWords.length - 1);
      const endWord = sentenceWords[endWordIndex];
      if (!endWord) {
        return { 
          start: sentence.startChar + startWord.startChar, 
          end: sentence.startChar + startWord.endChar, 
          topic: pos.topic 
        };
      }

      // Convert to absolute character positions in the full text
      // startWord.startChar and endWord.endChar are relative to sentence.startChar
      const absoluteStart = sentence.startChar + startWord.startChar;
      const absoluteEnd = sentence.startChar + endWord.endChar;

      return {
        start: absoluteStart,
        end: absoluteEnd,
        topic: pos.topic,
      };
    });
  }, [text, topicPositions, sentences]);

  // Filter positions by selected topic - only show highlights when a topic is selected
  const filteredPositions = useMemo(() => {
    if (!selectedTopic) return []; // Show nothing by default
    // Check if the topic matches (handle topics that might be combined with " / ")
    return charPositions.filter(pos => {
      const topics = pos.topic.split(' / ');
      return topics.includes(selectedTopic);
    });
  }, [charPositions, selectedTopic]);

  // When a topic is selected, extend highlights to cover full sentences
  const sentenceHighlights = useMemo(() => {
    if (!selectedTopic || filteredPositions.length === 0) return [];
    
    // Find all sentences that contain any of the filtered positions
    const sentenceIndices = new Set<number>();
    filteredPositions.forEach(pos => {
      // Find which sentence this position belongs to
      for (let i = 0; i < sentences.length; i++) {
        const sentence = sentences[i];
        if (pos.start >= sentence.startChar && pos.start < sentence.endChar) {
          sentenceIndices.add(i);
          break;
        }
      }
    });

    // Convert sentence indices to character positions
    return Array.from(sentenceIndices).map(index => {
      const sentence = sentences[index];
      return {
        start: sentence.startChar,
        end: sentence.endChar,
        topic: selectedTopic,
      };
    });
  }, [selectedTopic, filteredPositions, sentences]);

  // Use sentence highlights when topic is selected, otherwise use word-level highlights
  const positionsToHighlight = useMemo(() => {
    if (selectedTopic && sentenceHighlights.length > 0) {
      return sentenceHighlights;
    }
    return filteredPositions;
  }, [selectedTopic, sentenceHighlights, filteredPositions]);

  // Merge overlapping highlights and combine topics
  const mergedPositions = useMemo(() => {
    // Sort by start, then by end
    const sorted = [...positionsToHighlight].sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start;
      return a.end - b.end;
    });

    if (sorted.length === 0) return [];

    const merged: Array<{ start: number; end: number; topics: Set<string> }> = [];
    
    for (const pos of sorted) {
      if (merged.length === 0) {
        merged.push({
          start: pos.start,
          end: pos.end,
          topics: new Set([pos.topic]),
        });
        continue;
      }

      const last = merged[merged.length - 1];
      
      // Check if this position overlaps or is adjacent to the last one
      if (pos.start <= last.end) {
        // Merge: extend the end if needed and add topics
        last.end = Math.max(last.end, pos.end);
        last.topics.add(pos.topic);
      } else {
        // New non-overlapping position
        merged.push({
          start: pos.start,
          end: pos.end,
          topics: new Set([pos.topic]),
        });
      }
    }

    return merged.map(m => ({
      start: m.start,
      end: m.end,
      topic: Array.from(m.topics).join(' / '),
    }));
  }, [positionsToHighlight]);

  // Build array of text segments with highlighting info
  const segments = useMemo(() => {
    if (mergedPositions.length === 0) {
      return [{ text, isHighlighted: false }];
    }

    const result: Array<{ text: string; topic?: string; isHighlighted: boolean }> = [];
    let lastIndex = 0;

    mergedPositions.forEach((pos) => {
      // Add text before highlight
      if (pos.start > lastIndex) {
        result.push({
          text: text.substring(lastIndex, pos.start),
          isHighlighted: false,
        });
      }

      // Add highlighted text (only once per merged position)
      const highlightedText = text.substring(pos.start, pos.end);
      if (highlightedText.length > 0) {
        result.push({
          text: highlightedText,
          topic: pos.topic,
          isHighlighted: true,
        });
      }

      lastIndex = Math.max(lastIndex, pos.end);
    });

    // Add remaining text
    if (lastIndex < text.length) {
      result.push({
        text: text.substring(lastIndex),
        isHighlighted: false,
      });
    }

    return result.length > 0 ? result : [{ text, isHighlighted: false }];
  }, [text, mergedPositions]);

  return (
    <div
      style={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        lineHeight: 1.8,
        fontSize: '15px',
        fontFamily: 'inherit',
        color: isDark ? theme.colors.gray[0] : theme.colors.dark[7],
      }}
    >
      {segments.map((segment, index) => {
        if (segment.isHighlighted && segment.topic) {
          return (
            <Tooltip
              key={index}
              label={
                <div>
                  <Text size="sm" weight={600}>
                    Topic:
                  </Text>
                  <Text size="sm">{segment.topic}</Text>
                </div>
              }
              openDelay={200}
              position="top"
              withArrow
              withinPortal
            >
              <mark
                style={{
                  backgroundColor: isDark
                    ? theme.colors.blue[8]
                    : theme.colors.blue[2],
                  color: isDark
                    ? theme.colors.blue[0]
                    : theme.colors.blue[9],
                  padding: '2px 6px',
                  borderRadius: '4px',
                  cursor: 'help',
                  fontWeight: 600,
                  border: `1px solid ${
                    isDark ? theme.colors.blue[6] : theme.colors.blue[4]
                  }`,
                  margin: '0 1px',
                  display: 'inline-block',
                }}
              >
                {segment.text}
              </mark>
            </Tooltip>
          );
        }
        return <span key={index}>{segment.text}</span>;
      })}
    </div>
  );
};

export const RealtimeClassificationPage: FC = () => {
  const theme = useMantineTheme();
  const isDark = theme.colorScheme === 'dark';
  const [inputText, setInputText] = useState('');
  const [debouncedText] = useDebouncedValue(inputText, 500);

  // Split text into sentences (matching the API's sentence splitting logic)
  // The API uses autocorrect processing which splits on NEWSENTENCE characters
  // After processing, sentences are created by joining words with spaces
  // We approximate this by splitting on: . ! ? and \n (including multiple consecutive ones)
  const sentences = useMemo(() => {
    const result: Array<{ text: string; startChar: number; endChar: number }> = [];
    
    // Split on sentence-ending characters: . ! ? and newlines (\n, including multiple consecutive)
    // The API processes text into words first, then splits on single-character NEWSENTENCE words
    // We approximate by splitting on these characters directly
    let lastIndex = 0;
    
    // Match sentence-ending punctuation (. ! ?) or newlines (including multiple consecutive)
    // The + quantifier handles multiple consecutive characters (e.g., \n\n, !!!)
    const sentenceBoundaryRegex = /([.!?]+|\n+)/g;
    let match;

    while ((match = sentenceBoundaryRegex.exec(inputText)) !== null) {
      const boundaryStart = match.index;
      const boundaryEnd = match.index + match[0].length;
      
      // Get the sentence text up to and including the boundary
      const sentenceText = inputText.substring(lastIndex, boundaryEnd);
      
      // Only add non-empty sentences (after trimming whitespace)
      // This handles cases where there are multiple newlines/spaces creating empty segments
      const trimmedSentence = sentenceText.trim();
      if (trimmedSentence.length > 0) {
        result.push({
          text: sentenceText, // Keep untrimmed to preserve character positions
          startChar: lastIndex,
          endChar: boundaryEnd,
        });
      }
      
      lastIndex = boundaryEnd;
    }

    // Handle any remaining text after the last sentence boundary
    if (lastIndex < inputText.length) {
      const remaining = inputText.substring(lastIndex);
      const trimmedRemaining = remaining.trim();
      if (trimmedRemaining.length > 0) {
        result.push({
          text: remaining, // Keep untrimmed
          startChar: lastIndex,
          endChar: inputText.length,
        });
      }
    }

    // If no sentence boundaries found, treat entire text as one sentence
    if (result.length === 0) {
      return [{ text: inputText, startChar: 0, endChar: inputText.length }];
    }

    return result;
  }, [inputText]);

  const { data: classificationResults, isLoading } = useQuery({
    queryKey: ['classify', debouncedText],
    queryFn: () => ClassificationAPI.classifyText(debouncedText),
    enabled: debouncedText.trim().length > 0,
    retry: false,
  });

  // Flatten all topic positions from all sentences
  const allTopicPositions = useMemo(() => {
    if (!classificationResults) return [];
    return classificationResults.flatMap((result) => result.topicPositions);
  }, [classificationResults]);

  // Extract all unique topics
  const allTopics = useMemo(() => {
    const topicSet = new Set<string>();
    allTopicPositions.forEach(pos => {
      // Split topics that are combined with " / "
      const topics = pos.topic.split(' / ');
      topics.forEach(topic => topicSet.add(topic.trim()));
    });
    return Array.from(topicSet).sort();
  }, [allTopicPositions]);

  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);

  return (
    <SectionWrapper isFullHeight showBreadcrumbs>
      <Stack spacing="lg" style={{ height: '100%' }}>
        <div>
          <Title order={2}>Realtime Classification</Title>
          <Text color="dimmed" size="sm">
            Type text in the left panel to see real-time topic classification
            with highlighted positions.
          </Text>
        </div>

        <Grid gutter="md" style={{ flex: 1, minHeight: 0 }}>
          <Grid.Col span={6}>
            <Paper
              shadow="sm"
              p="md"
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <Title order={4} mb="md">
                Input Text
              </Title>
              <div style={{ flex: 1, minHeight: 0 }}>
                <Textarea
                  placeholder="Start typing to see real-time classification..."
                  value={inputText}
                  onChange={(e) => setInputText(e.currentTarget.value)}
                  styles={{
                    input: {
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      height: '100%',
                      resize: 'none',
                    },
                    root: {
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    },
                    wrapper: {
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    },
                  }}
                />
              </div>
            </Paper>
          </Grid.Col>

          <Grid.Col span={6}>
            <Paper
              shadow="sm"
              p="md"
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Title order={4} mb="md">
                Classified Output
              </Title>
              
              {isLoading ? (
                <Stack align="center" justify="center" style={{ flex: 1 }}>
                  <Loader size="md" />
                  <Text color="dimmed" size="sm">
                    Classifying text...
                  </Text>
                </Stack>
              ) : inputText.trim().length === 0 ? (
                <Text color="dimmed" size="sm" style={{ fontStyle: 'italic' }}>
                  Enter some text in the left panel to see classification results.
                </Text>
              ) : allTopicPositions.length === 0 ? (
                <Text color="dimmed" size="sm" style={{ fontStyle: 'italic' }}>
                  No topics found in the text.
                </Text>
              ) : (
                <Stack spacing="md" style={{ flex: 1, minHeight: 0 }}>
                  {/* Topic Filter Section */}
                  <Paper
                    p="sm"
                    style={{
                      backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.gray[1],
                      borderRadius: '4px',
                    }}
                  >
                    <Text size="sm" weight={600} mb="xs">
                      Filter by Topic:
                    </Text>
                    <ScrollArea style={{ maxHeight: 120 }}>
                      <Group spacing="xs">
                        {allTopics.map((topic) => (
                          <Button
                            key={topic}
                            size="xs"
                            variant={selectedTopic === topic ? 'filled' : 'outline'}
                            onClick={() => setSelectedTopic(topic)}
                          >
                            {topic}
                          </Button>
                        ))}
                      </Group>
                    </ScrollArea>
                  </Paper>

                  {/* Highlighted Text Section */}
                  <div
                    style={{
                      flex: 1,
                      overflow: 'auto',
                      padding: '8px',
                      backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.gray[0],
                      borderRadius: '4px',
                      minHeight: 0,
                    }}
                  >
                    <HighlightedText
                      text={inputText}
                      topicPositions={allTopicPositions}
                      selectedTopic={selectedTopic}
                      sentences={sentences}
                    />
                  </div>
                </Stack>
              )}
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>
    </SectionWrapper>
  );
};

export default RealtimeClassificationPage;
